import type {ReactNode} from "react";
import {Box, Flex, Text} from "@chakra-ui/react";
import type {BoxProps, FlexProps} from "@chakra-ui/react";

// --- Container ---

const Container = ({children, ...rest}: BoxProps) => (
  <Box bg="white" borderRadius="md" border="1px solid {colors.gray.200}" overflow="hidden" {...rest}>
    {children}
  </Box>
);

// --- ScrollArea ---

const ScrollArea = ({minW, children, ...rest}: {minW: string} & BoxProps) => (
  <Box overflowX="auto" {...rest}>
    <Box minW={minW}>{children}</Box>
  </Box>
);

// --- Header ---

type GridProps = {
  columns: string;
  px?: number;
  py?: number;
  gap?: number;
  fontSize?: string;
  children: ReactNode;
} & Omit<BoxProps, "display" | "gridTemplateColumns">;

const Header = ({columns, px = 6, py = 3, gap = 3, fontSize = "sm", children, ...rest}: GridProps) => (
  <Box
    display="grid"
    gridTemplateColumns={columns}
    gap={gap}
    alignItems="center"
    px={px}
    py={py}
    borderBottom="1px solid {colors.gray.200}"
    bg="white"
    fontWeight="600"
    fontSize={fontSize}
    color="gray.500"
    {...rest}
  >
    {children}
  </Box>
);

// --- Row ---

type RowProps = {
  columns: string;
  px?: number;
  py?: number;
  gap?: number;
  alignItems?: string;
  borderVariant?: "notLast" | "always";
  hoverable?: boolean;
  clickable?: boolean;
  children: ReactNode;
} & Omit<BoxProps, "display" | "gridTemplateColumns">;

const Row = ({
  columns,
  px = 6,
  py = 3,
  gap = 3,
  alignItems = "center",
  borderVariant = "notLast",
  hoverable = true,
  clickable = true,
  children,
  ...rest
}: RowProps) => (
  <Box
    display="grid"
    gridTemplateColumns={columns}
    gap={gap}
    alignItems={alignItems}
    px={px}
    py={py}
    {...(borderVariant === "always"
      ? {borderBottom: "1px solid {colors.gray.200}"}
      : {_notLast: {borderBottom: "1px solid {colors.gray.100}"}})}
    {...(hoverable ? {_hover: {bg: "gray.50"}} : {})}
    {...(clickable ? {cursor: "pointer"} : {})}
    {...rest}
  >
    {children}
  </Box>
);

// --- GroupHeader ---

const GroupHeader = ({isFirst, children, ...rest}: {isFirst?: boolean; children: ReactNode} & BoxProps) => (
  <Box
    py={2}
    bg="gray.50"
    borderTop={isFirst ? undefined : "1px solid {colors.gray.200}"}
    borderBottom="1px solid {colors.gray.200}"
    {...rest}
  >
    {children}
  </Box>
);

// --- Empty ---

const Empty = ({children, ...rest}: BoxProps) => (
  <Box py={8} textAlign="center" {...rest}>
    <Text fontSize="sm" color="gray.500">
      {children}
    </Text>
  </Box>
);

// --- ActionBar ---

const ActionBar = ({children, ...rest}: FlexProps) => (
  <Flex
    align="center"
    justify="flex-end"
    px={6}
    py={2}
    borderBottom="1px solid {colors.gray.200}"
    bg="gray.50"
    {...rest}
  >
    {children}
  </Flex>
);

// --- Namespace export ---

export const DataGrid = {Container, ScrollArea, Header, Row, GroupHeader, Empty, ActionBar};
